import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { ContactDecisionMakerSendFailure } from '#src/app/shared/utils/contactDecisionMaker';

import type {
	CreateDecisionMakerMessageErrorReason,
	CreateDecisionMakerMessageMutation,
	CreateDecisionMakerMessageMutationVariables,
} from './api.graphql';
import { CreateDecisionMakerMessage } from './api.graphql';

export type SendMessageResult = { status: 'sent' } | { status: 'failed'; failure: ContactDecisionMakerSendFailure };

/**
 * The reasons that are about the message itself, so editing it is the way out. Every other reason
 * defers to the resolver's own `retryable` flag: true means the same body may go through, false means
 * nothing the supporter can type will change the answer.
 */
const REJECTED_REASONS: ReadonlySet<CreateDecisionMakerMessageErrorReason> = new Set([
	'MESSAGE_BLANK',
	'MESSAGE_TOO_LONG',
]);

function classifyFailure(
	reason: CreateDecisionMakerMessageErrorReason,
	retryable: boolean,
): ContactDecisionMakerSendFailure {
	if (REJECTED_REASONS.has(reason)) return 'rejected';
	return retryable ? 'retryable' : 'blocked';
}

/**
 * `sending` refuses `dismiss` so a late success cannot be dropped, and Send is loading-disabled — so a
 * request that never settles would leave a spinner with no way out but a page reload.
 */
const SEND_TIMEOUT_MS = 15000;

export type Params = {
	petitionId: string;
	decisionMakerId: string;
	body: string;
};

/**
 * Sends the supporter's message, and — unlike the draft mutation next door — **reports failure
 * honestly**. A draft that fails can fall back to a template because the supporter still ends up with
 * something sendable; a send that fails has to be told, or they believe their message went out.
 *
 * The resolver's `retryable` is not enough on its own: it is false for both "your message is too
 * long" and "you are no longer logged in", and those need different screens. FORBIDDEN and NOT_FOUND
 * are therefore split out as `blocked` — an edit-and-retry prompt would loop a supporter whose
 * session expired mid-compose, failing identically on every attempt with nothing telling them why.
 *
 * A client-side timeout is its own outcome again: the request is aborted, but whether the server
 * stored the message before that is unknowable, so it resolves to `unconfirmed` rather than inviting
 * a retry that would deliver a second message.
 */
export async function createDecisionMakerMessage(
	{ petitionId, decisionMakerId, body }: Params,
	{ gql: { fetch }, errorReporter }: UtilityContext,
): Promise<SendMessageResult> {
	// Abort rather than race the request: there is no idempotency key on the input and `createComment`
	// does not dedupe, so letting an abandoned request finish is how a supporter sends twice.
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);

	try {
		const { data } = await fetch<CreateDecisionMakerMessageMutation, CreateDecisionMakerMessageMutationVariables>({
			query: CreateDecisionMakerMessage,
			variables: { input: { petitionId, decisionMakerId, body } },
			// Must match the mutation's @restrictPath on the middle-end resolver.
			path: '/create-decision-maker-message',
			// Unlike the draft mutation next door, a failure here has no usable fallback — let it throw and
			// be caught below, rather than resolving with nothing and pretending the message went out.
			rejectOnError: true,
			// @restrictCount is a per-request alias guard, so this must not share a request with anything else.
			batched: false,
			// Apollo's HttpLink merges per-operation `fetchOptions` into the request.
			context: { fetchOptions: { signal: controller.signal } },
		});

		const result = data.createDecisionMakerMessage;

		if (result.__typename === 'CreateDecisionMakerMessageError') {
			// Not reported here. Every reason the resolver returns it has already classified, and it
			// reports the ones worth an alert itself — a second synthetic, cause-less Error would put two
			// events in two projects for one incident, multiplied by every retry the supporter makes.
			return { status: 'failed', failure: classifyFailure(result.reason, result.retryable) };
		}

		return { status: 'sent' };
	} catch (error) {
		// Transport and GraphQL errors are already reported by core's fetch wrapper and error link;
		// reporting them again here would put two events in Sentry per incident, times every retry. The
		// abort is the one failure that originates here, and core samples network errors down.
		if (controller.signal.aborted) {
			void errorReporter.report({
				error: error instanceof Error ? error : new Error(String(error)),
				params: { timedOut: 'true' },
			});
		}

		// A timeout says nothing about whether the message was stored — only that we stopped waiting.
		// Calling it retryable would invite the duplicate the abort exists to prevent.
		return { status: 'failed', failure: controller.signal.aborted ? 'unconfirmed' : 'retryable' };
	} finally {
		clearTimeout(timer);
	}
}
